import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, X } from "lucide-react";
import { toast } from "@/components/ui/sonner";

interface SessionPreviewData {
  documentId?: string; // For updates only
  programKey: string;
  startDate: string;
  endDate: string;
  [key: string]: any; // For dynamic fields like title, url, price, etc.
}

interface AISessionFetchPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  previewData: SessionPreviewData[];
  selectedFields: string[];
  action: "update" | "create";
  onApplyChanges?: (data: SessionPreviewData[]) => void;
}

export const AISessionFetchPreview: React.FC<AISessionFetchPreviewProps> = ({
  isOpen,
  onClose,
  onBack,
  previewData,
  selectedFields,
  action,
  onApplyChanges
}) => {
  if (!isOpen) return null;

  console.log("[AISessionFetchPreview] Component rendering with:", {
    isOpen,
    previewDataLength: previewData?.length,
    firstRow: previewData?.[0],
    selectedFields,
    action
  });

  // Auto-detect available fields from the actual data
  const detectAvailableFields = () => {
    if (!previewData || previewData.length === 0) {
      console.log("[AISessionFetchPreview] No preview data available");
      return [];
    }
    
    const allFields = new Set<string>();
    previewData.forEach((row, index) => {
      console.log(`[AISessionFetchPreview] Row ${index} keys:`, Object.keys(row));
      Object.keys(row).forEach(key => {
        // Skip basic fields that are always shown
        if (!['documentId', 'id', 'programKey', 'variationKey', 'startDate', 'endDate'].includes(key)) {
          allFields.add(key);
        }
      });
    });
    
    const fieldsArray = Array.from(allFields).sort();
    console.log("[AISessionFetchPreview] Detected fields:", fieldsArray);
    return fieldsArray;
  };

  const availableFields = detectAvailableFields();
  const displayFields = availableFields.length > 0 ? availableFields : selectedFields;

  console.log("[AISessionFetchPreview] Final display fields:", displayFields);
  console.log("[AISessionFetchPreview] Preview data sample:", previewData?.slice(0, 2));

  const copyToClipboard = async () => {
    try {
      // Create a formatted table string
      const headers = action === "update" 
        ? ["Document ID", "Program", "Start Date", "End Date", ...displayFields]
        : ["Program", "Start Date", "End Date", ...displayFields];
      
      const rows = previewData.map(row => {
        const baseColumns = action === "update" 
          ? [row.documentId || row.id || "", row.programKey || row.variationKey || "", row.startDate, row.endDate]
          : [row.programKey || row.variationKey || "", row.startDate, row.endDate];
        
        const dataColumns = displayFields.map(field => row[field] || "");
        
        return [...baseColumns, ...dataColumns];
      });

      const tableText = [headers, ...rows]
        .map(row => row.join("\t"))
        .join("\n");

      await navigator.clipboard.writeText(tableText);
      toast.success("Preview data copied to clipboard");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleApplyChanges = () => {
    if (onApplyChanges) {
      onApplyChanges(previewData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-[90vw] h-[80vh] flex flex-col">
        <CardHeader className="flex-shrink-0 border-b">
          <div className="flex justify-between items-center">
            <CardTitle>
              AI Fetch Preview ({action === "update" ? "Update" : "Create"} {previewData.length} sessions)
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-auto p-6">
          {/* Debug section - remove after fixing */}
          <div className="mb-4 p-4 bg-gray-100 rounded text-sm">
            <div><strong>Debug Info:</strong></div>
            <div>Preview Data Length: {previewData?.length || 0}</div>
            <div>Display Fields: {displayFields.join(', ') || 'none'}</div>
            <div>Action: {action}</div>
            {previewData?.length > 0 && (
              <div className="mt-2">
                <strong>First Row Keys:</strong> {Object.keys(previewData[0]).join(', ')}
              </div>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  {action === "update" && <th className="border border-gray-300 p-2 text-left">Document ID</th>}
                  <th className="border border-gray-300 p-2 text-left">Program</th>
                  <th className="border border-gray-300 p-2 text-left">Start Date</th>
                  <th className="border border-gray-300 p-2 text-left">End Date</th>
                  {displayFields.map(field => (
                    <th key={field} className="border border-gray-300 p-2 text-left">
                      {field}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {action === "update" && (
                      <td className="border border-gray-300 p-2 font-mono text-xs">
                        {row.documentId || row.id}
                      </td>
                    )}
                    <td className="border border-gray-300 p-2">{row.programKey || row.variationKey}</td>
                    <td className="border border-gray-300 p-2">{row.startDate}</td>
                    <td className="border border-gray-300 p-2">{row.endDate}</td>
                    {displayFields.map(field => (
                      <td key={field} className={`border border-gray-300 p-2 ${field.includes('_new') ? 'bg-yellow-50' : ''}`}>
                        {row[field] || ""}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
        
        <div className="flex-shrink-0 border-t p-6">
          <div className="flex justify-between items-center">
            <Button variant="outline" onClick={copyToClipboard}>
              <Copy className="h-4 w-4 mr-2" />
              Copy to Clipboard
            </Button>
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={onBack || onClose}>
                {onBack ? "Back" : "Close"}
              </Button>
              {onApplyChanges && (
                <Button onClick={handleApplyChanges}>
                  Apply to Database
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}; 